import {useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {fetchCompetitionModuleData} from 'app/queries/districtCompetitions';
import {AxiosError} from 'axios';
import {DefaultApiErrorResponse} from 'app/common/errorTypes';
import DistrictCompetitionsResponse from 'app/components/competitionModule/models/DistrictCompetitionsResponse';
import {useMemo} from 'react';
import InfiniteCompetitionModules from 'app/components/competitionModule/models/InfiniteCompetitionModules';
import FuPaApiError from 'app/models/error/FuPaApiError';

type ReturnType = {
  infiniteResult:
    | UseInfiniteQueryResult<DistrictCompetitionsResponse, AxiosError<DefaultApiErrorResponse, any>>
    | FuPaApiError;
  infiniteCompetitionModules: InfiniteCompetitionModules;
};

export const useQueryCompetitionModules = (
  slug: string,
  initialData: DistrictCompetitionsResponse | FuPaApiError | undefined
): ReturnType => {
  const infiniteResult = useInfiniteQuery({
    queryKey: ['competitions', {slug}],
    queryFn: ({pageParam = null}) => fetchCompetitionModuleData(slug, pageParam),
    enabled: !(initialData instanceof FuPaApiError),
    getNextPageParam: lastPage => {
      if (lastPage instanceof FuPaApiError) {
        return undefined;
      }
      return lastPage.getNextPageParam();
    },
    initialData:
      initialData && initialData instanceof DistrictCompetitionsResponse
        ? {pages: [initialData], pageParams: [undefined]}
        : undefined,
    onError: (error: AxiosError<DefaultApiErrorResponse>) => error, // only used for typing purposes
  });

  return useMemo(
    () => ({
      infiniteResult: initialData instanceof FuPaApiError ? initialData : infiniteResult,
      infiniteCompetitionModules: new InfiniteCompetitionModules(
        initialData instanceof FuPaApiError ? undefined : infiniteResult.data
      ),
    }),
    [initialData, infiniteResult]
  );
};
